import React from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import colors from '../../../theme/colors';

type CustomButtonColor = {
  default: string;
  hover: string;
  active: string;
  background?: string;
};

const DEFAULT_COLORS: CustomButtonColor = {
  default: colors.bright_turquoise,
  hover: colors.bright_turquoise,
  active: colors.madison,
  background: '',
};

const useStyles = makeStyles({
  baseButton: {
    borderRadius: 25,
    fontWeight: 'bold',
    fontSize: 14,
    fontStyle: 'normal',
    lineHeight: '24px',
    paddingLeft: 30,
    paddingRight: 30,
    height: 50,
  },
  defaultButton: (usedColor: CustomButtonColor) => ({
    backgroundColor:
      usedColor.background === undefined || usedColor.background === ''
        ? usedColor.default
        : usedColor.background,
    color: colors.white,
    '&:hover': {
      backgroundColor: usedColor.hover,
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
    },
    '&:disabled': {
      backgroundColor: colors.gallery,
      color: colors.silver,
    },
    '&:active': {
      backgroundColor: usedColor.active,
    },
  }),
  outlinedButton: (usedColor: CustomButtonColor) => ({
    backgroundColor:
      usedColor.background === undefined ? 'transparent' : usedColor.background,
    color: usedColor.default,
    border: `1px solid ${usedColor.default}`,
    '&:hover': {
      color: usedColor.default,
      backgroundColor: 'rgba(0, 194, 255, 0.12)',
    },
    '&:disabled': {
      backgroundColor: 'transparent',
      border: `1px solid ${colors.silver}`,
      color: colors.silver,
    },
    '&:active': {
      backgroundColor: 'rgba(23, 73, 160, 0.12)',
      color: usedColor.active,
      border: `1px solid ${usedColor.active}`,
    },
  }),
  borderlessButton: (usedColor: CustomButtonColor) => ({
    background:
      usedColor.background === undefined ? 'transparent' : usedColor.background,
    color: usedColor.default,
    border: 'none',
    '&:disabled': {
      color: colors.silver,
    },
    '&:active': {
      color: usedColor.active,
    },
  }),
});

export type ButtonProps = {
  /** The identifying the button */
  id?: string;
  /** The button's content */
  children?: React.ReactNode;
  /** If true, the button cannot be clicked, and the disabled style will be shown */
  disabled?: boolean;
  /** If true, the button will try to fit its entire container. Overwrites forcedWidth */
  fullWidth?: boolean;
  /** The button type (submit for forms, button for most other cases) */
  type?: 'submit' | 'button' | 'reset';
  /** Which button visual variant to render */
  variant?: 'default' | 'outlined' | 'borderless';
  /** Called when the button is clicked. */
  onClick?: () => void;
  /** HEX that will overwrite the default button color */
  customColor?: CustomButtonColor;
  /**
   * If defined, the button will use this width instead of trying to calculate it itself.
   */
  forcedWidth?: number;
};

const DefaultButton = ({
  id,
  children,
  onClick,
  disabled = false,
  fullWidth = false,
  type = 'button',
  variant = 'default',
  customColor = DEFAULT_COLORS,
  forcedWidth,
}: ButtonProps): JSX.Element => {
  const classes = useStyles(customColor);
  let modifiedButtonClass: string;
  switch (variant) {
    case 'borderless':
      modifiedButtonClass = classes.borderlessButton;
      break;
    case 'outlined':
      modifiedButtonClass = classes.outlinedButton;
      break;
    default:
      modifiedButtonClass = classes.defaultButton;
  }

  return (
    <Button
      id={id}
      disabled={disabled}
      disableElevation
      disableRipple
      className={clsx(classes.baseButton, modifiedButtonClass)}
      onClick={onClick}
      type={type}
      fullWidth={fullWidth}
      style={forcedWidth && !fullWidth ? { width: forcedWidth } : {}}
    >
      {children}
    </Button>
  );
};

export default DefaultButton;
